import { DateTime } from 'luxon'

import { IanaZone, IsoDate, IsoInstant, LocalTime } from '../types'

/**
 * Parse a UTC instant from the API into a DateTime expressed in the given zone
 *
 * @param instant
 * @param zone
 * @returns
 */
export function instantToDateTime(
  instant: IsoInstant,
  zone: IanaZone,
): DateTime {
  return DateTime.fromISO(instant, { zone })
}

/**
 * Serialise any DateTime to the api instant format.
 *
 * @param dt
 * @returns
 */
export function fromDateTime(dt: DateTime): IsoInstant {
  return IsoInstant.parse(
    dt
      .toUTC()
      .startOf('second')
      .set({ millisecond: 0 })
      .toISO({ suppressMilliseconds: true }),
  )
}

/**
 * Combine a calendar date and wall-clock time in a zone into a DateTime.
 *
 * @param date
 * @param time
 * @param zone
 * @returns
 */
export function wallTimeToInstant(
  date: IsoDate,
  time: LocalTime,
  zone: IanaZone,
): DateTime {
  return DateTime.fromISO(`${date}T${time}`, { zone })
}

/**
 * True when the wall time exists in that zone on that date.
 * Returns false for the hour skipped by a spring-foward transition (Luxon silently shifts
 * such times forward. Call this first to reject instead).
 *
 * @param date
 * @param time
 * @param zone
 * @returns
 */
export function wallTimeExists(
  date: IsoDate,
  time: LocalTime,
  zone: IanaZone,
): boolean {
  const dt = wallTimeToInstant(date, time, zone)
  return dt.toISODate() === date && dt.toFormat('HH:mm') === time
}

/**
 * True when the wall time occurs twice (the repeated hour of a fall-back transition).
 * 
 * @param date 
 * @param time 
 * @param zone 
 * @returns 
 */
export function wallTimeIsAmbiguous(
  date: IsoDate,
  time: LocalTime,
  zone: IanaZone,
): boolean {
  const dt = wallTimeToInstant(date, time, zone)
  const oneHourLater = dt.plus({ hours: 1 })
  return (
    oneHourLater.toFormat('HH:mm') === time && oneHourLater.toISODate() === date
  )
}

export function toIsoDate(dt: DateTime): IsoDate {
    return IsoDate.parse(dt.toISODate())
}

export function toLocalTime(dt: DateTime): LocalTime {
    return LocalTime.parse(dt.toFormat('HH:mm'))
}

/**
 * Today's calendar date at a location.
 * 
 * @param zone 
 * @param now 
 * @returns 
 */
export function todayIn(zone: IanaZone, now: DateTime = DateTime.now()): IsoDate {
    return toIsoDate(now.setZone(zone))
}
